'use client'

import { useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalculatorIcon,
  CalendarIcon,
  DownloadIcon,
  EyeIcon,
  HashIcon,
  ListIcon,
  MoreHorizontalIcon,
  PackageIcon,
  RefreshCwIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  WaypointsIcon
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { JobStatusEnum, SortEnum } from '@/enums'
import {
  useDeleteProductExcelExport,
  useProductExcelExports,
  useBulkDeleteProductExcelExports,
  useCreateProductExcelExport
} from '@/hooks'
import { useFilesStore, useProductExcelExportsStore } from '@/store'
import { ProductExcelExportsFilterParams } from '@/types/product-excel-export'
import {
  ConfirmationDialog,
  DeleteConfirmationDialog,
  JobStatusBadge,
  LoadingState,
  ProductExcelExportsFilters,
  SortableHeader
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'
import { formatDateTime, formatNumber, tOptional } from '@/lib/utils'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteProductExcelExportId, setDeleteProductExcelExportId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const [regenerateConfirmProductExcelExport, setRegenerateConfirmProductExcelExport] = useState<any | null>(null)

  const { mutate: deleteProductExcelExportMutate, isPending: isDeleting } = useDeleteProductExcelExport()
  const { mutate: bulkDeleteProductExcelExportsMutate, isPending: isBulkDeleting } = useBulkDeleteProductExcelExports()
  const { mutate: createProductExcelExportMutate, isPending: isRegenerating } = useCreateProductExcelExport()
  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)

  const selectedProductExcelExportIds = useProductExcelExportsStore(state => state.selectedProductExcelExportIds)
  const toggleProductExcelExportSelection = useProductExcelExportsStore(
    state => state.toggleProductExcelExportSelection
  )
  const selectAllProductExcelExports = useProductExcelExportsStore(state => state.selectAllProductExcelExports)
  const deselectAllProductExcelExports = useProductExcelExportsStore(state => state.deselectAllProductExcelExports)

  const filters = useMemo<ProductExcelExportsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    // Parse statuses from URL (comma-separated string to array)
    const statusesParam = params.get('statuses')
    const statuses = statusesParam ? statusesParam.split(',').filter(Boolean) : undefined

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      statuses,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: productExcelExportsData, isLoading, error, refetch, isFetching } = useProductExcelExports(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<ProductExcelExportsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle array values (like statuses)
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ProductExcelExportsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/export/excel')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (productExcelExportId: string) => {
    router.push(`/products/export/excel/${productExcelExportId}`)
  }

  const handleDelete = (productExcelExportId: string) => {
    setDeleteProductExcelExportId(productExcelExportId)
  }

  const handleRegenerate = (productExcelExport: any) => {
    setRegenerateConfirmProductExcelExport(productExcelExport)
  }

  const confirmRegenerate = () => {
    const target = regenerateConfirmProductExcelExport
    if (!target?.filters) {
      setRegenerateConfirmProductExcelExport(null)
      return
    }
    createProductExcelExportMutate(target.filters, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['productExcelExports'] })
        toast.success(t('created_successfully'))
        handleFiltersChange({ page: 1 })
        setRegenerateConfirmProductExcelExport(null)
      },
      onError: (error: any) => {
        console.error('Error regenerating productExcelExport:', error)
        toast.error(t('error_creating'))
        setRegenerateConfirmProductExcelExport(null)
      }
    })
  }

  const renderActionItems = (productExcelExport: any, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(productExcelExport.id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('excel_export_details')}
        </ItemComponent>
        {productExcelExport.status === JobStatusEnum.COMPLETED && productExcelExport.file && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              addToDownloadQueue(productExcelExport.file)
            }}>
            <DownloadIcon className="h-4 w-4" />
            {t('download')}
          </ItemComponent>
        )}
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleRegenerate(productExcelExport)
          }}
          disabled={isRegenerating}>
          <RefreshCwIcon className="h-4 w-4" />
          {t('regenerate')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(productExcelExport.id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteProductExcelExportId) {
      deleteProductExcelExportMutate(deleteProductExcelExportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productExcelExports'] })
          toast.success(t('deleted_successfully'))
          setDeleteProductExcelExportId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting model:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteProductExcelExportId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedProductExcelExportIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteProductExcelExports = () => {
    if (selectedProductExcelExportIds.length > 0) {
      bulkDeleteProductExcelExportsMutate(selectedProductExcelExportIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['productExcelExports'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (productExcelExportsData && productExcelExportsData.items.length > 0) {
      if (selectedProductExcelExportIds.length === productExcelExportsData.items.length) {
        // If all productExcelExports are selected, deselect all
        deselectAllProductExcelExports()
      } else {
        // Otherwise, select all productExcelExports
        selectAllProductExcelExports(productExcelExportsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListIcon className="h-5 w-5" />
                {t('excel_exports')}
              </CardTitle>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const currentParams = searchParams.toString()
                  const targetUrl = `/products${currentParams ? `?${currentParams}` : ''}`
                  router.push(targetUrl)
                }}>
                <PackageIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('products')}</span>
              </Button>
              <Button onClick={() => router.push('/products/export/excel/create')}>
                <SquarePlusIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('create_excel_export')}</span>
              </Button>
              {selectedProductExcelExportIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedProductExcelExportIds.length})</span>
                  </div>
                </Button>
              )}
              <ProductExcelExportsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {productExcelExportsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={
                                productExcelExportsData &&
                                productExcelExportsData.items.length > 0 &&
                                selectedProductExcelExportIds.length === productExcelExportsData.items.length
                              }
                              onCheckedChange={() => {
                                handleSelectAll()
                              }}
                              onClick={e => {
                                e.stopPropagation()
                              }}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="total"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('total')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="status"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <WaypointsIcon className="h-4 w-4" />
                            {t('status')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productExcelExportsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        productExcelExportsData.items.map((productExcelExport, index) => (
                          <ContextMenu key={productExcelExport.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(productExcelExport.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={selectedProductExcelExportIds.includes(productExcelExport.id)}
                                      onCheckedChange={() => toggleProductExcelExportSelection(productExcelExport.id)}
                                      onClick={e => {
                                        e.stopPropagation()
                                      }}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span className="text-muted-foreground text-xs">{productExcelExport.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productExcelExport.total, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div>
                                        <JobStatusBadge status={productExcelExport.status} />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {productExcelExport.message ? tOptional(t, productExcelExport.message) : ''}
                                    </TooltipContent>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(productExcelExport.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(productExcelExport.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(productExcelExport, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(productExcelExport, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={productExcelExportsData.page}
                  totalPages={productExcelExportsData.pages}
                  totalItems={productExcelExportsData.total}
                  pageSize={productExcelExportsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={regenerateConfirmProductExcelExport !== null}
        onCancel={() => setRegenerateConfirmProductExcelExport(null)}
        onConfirm={confirmRegenerate}
        description={t('regenerate_export_confirmation')}
        confirmText={t('regenerate')}
        isLoading={isRegenerating}
      />

      <DeleteConfirmationDialog
        open={deleteProductExcelExportId !== null}
        onCancel={() => setDeleteProductExcelExportId(null)}
        onConfirm={confirmDeleteMessage}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteProductExcelExports}
        isDeleting={isBulkDeleting}
      />
    </>
  )
}

export default List
